// Background answer-save queue (perf P1 — "Make Next navigation instant").
//
// Before: clicking "Next" awaited POST /answer, then navigated, then awaited a
// cold GET /question — two serialized round-trips of frozen UI (~300–500 ms on
// prod from Italy). This module removes the POST leg from the critical path:
// the candidate flow ENQUEUES the durable save and navigates immediately, while
// the POST lands in the background.
//
// Correctness contract (candidate answers must never be lost or reordered):
//   1. Tasks run STRICTLY SERIALLY, FIFO. Two saves for the same position
//      (answer → back → edit → forward) therefore persist in click order, so
//      last-write-wins holds. Different positions are independent rows, but
//      serial execution costs little and keeps the ordering guarantee total.
//   2. Transient failures (network throw / 502·503·504 / 500 — Fly cold proxy,
//      Neon reset) retry with backoff, so a blip self-heals instead of dropping
//      the answer. Terminal 4xx (400/404/409/410/422) never retry — the attempt
//      is finalized, superseded, forward-locked, or the payload is invalid;
//      retrying can't help.
//   3. flushAnswerSaves() drains the queue (in-flight + waiting). Every action
//      that depends on answers being durable server-side — POST /submit and
//      POST /advance-section — awaits it FIRST. The final submit fires from the
//      Review / SectionReview screens (separate components from Question), which
//      is exactly why the queue lives at module scope: it survives the unmount.
//   4. OPS-28 #1: a save abandoned after its retries is NOT forgotten. It is
//      kept as a FAILURE (`answerSaveFailures()`), reported to Sentry once, and
//      the Review / SectionReview screens refuse the final Submit while one is
//      pending — `retryAnswerSaveFailures()` re-runs the kept tasks, in their
//      original order, with the same payload (the B5 sessionStorage keep for
//      the position survives alongside: the task clears it only on success).
//      Before this, the cache-sync showed the answer as saved and /submit
//      graded whatever the server held — an optional or code answer as blank.
//   5. R13 (pre-ship 2026-10-18): a kept 400 / 422 whose Retry is refused
//      AGAIN is let go. The retry re-sends the very same bytes, so a third try
//      cannot land either, and holding the Submit on it locked the candidate
//      out for good. It moves to `answerSaveRefusals()` — reported, shown as a
//      warning on the review screens, never holding the Submit. "The same
//      bytes" is the kept task, or a new one with the same `payloadPrint`
//      (the per_question screen re-saves on every press).
//   6. R13b (pre-ship 2026-10-25): a save whose whole ladder ended on a 5xx is
//      kept like a blip (it used to be dropped silently), and — so a
//      deterministic 500 cannot hold the Submit for good — the same bytes
//      spending a second ladder on a 5xx are let go exactly as in 5. A ladder
//      that ends on the network (no answer at all) stays kept: it may come back,
//      and so does one that ends on a 429 (verify 2026-10-26: it was dropped).
//
// The queue is deliberately NOT reset between attempts: a retake is a fresh page
// load (new token) → module reset. Each task closes over its own attemptId, so a
// stray task always targets the attempt it was created for.

import { ApiError } from "./api";
import { captureSilentFailure } from "./observability";

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// Backoffs between retry attempts; length caps the retry count (6 attempts,
// ~27.6 s of backoff before a task is abandoned). OPS-22: the old ladder
// (400/1200/3000, ~4.6 s) was shorter than a backend restart — a single-machine
// deploy or a Fly restart is 5–30 s of 502/503 — so an in-flight save was
// dropped SILENTLY (the cache-sync still showed it as saved). 27.6 s rides
// out a restart; a serially-blocked queue is fine — a later save simply waits,
// preserving order.
export const RETRY_BACKOFF_MS = [400, 1200, 3000, 8000, 15000];

// Hard ceiling per attempt. fetch() has no default timeout, so a dead socket
// would neither resolve nor reject — hanging the task and, in turn, any
// flushAnswerSaves() barrier (which would freeze the Review screen). On timeout
// we ABORT the request (not just reject the wrapper): a detached slow POST that
// resolved 15s late could otherwise land AFTER a newer save for the same
// position (a back-nav re-edit) and silently overwrite it with the stale value —
// or land after /submit and desync the graded score. Aborting kills the request
// so it can never land; combined with the strictly-serial chain (a later save
// never starts until the earlier one has settled), the newest write always wins.
const ATTEMPT_TIMEOUT_MS = 10_000;

class SaveTimeoutError extends Error {
  constructor() {
    super("answer save timed out");
    this.name = "SaveTimeoutError";
  }
}

// Race one attempt against the timeout. `controller` owns the underlying fetch;
// on timeout we abort it so nothing can land late. Each retry gets a FRESH
// controller (see runWithRetry), so aborting a timed-out attempt never kills the
// next one.
function withTimeout(
  task: (signal: AbortSignal) => Promise<unknown>,
  controller: AbortController,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new SaveTimeoutError());
    }, ATTEMPT_TIMEOUT_MS);
    task(controller.signal).then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

// 4xx statuses that finalize/reject a save so a replay can't help: bad/invalid
// payload (400/422), attempt gone (404), finalized (409), superseded/expired
// (410).
const TERMINAL_STATUSES = new Set([400, 404, 409, 410, 422]);

// A save is terminally lost (do not retry) when the server says the attempt is
// gone/finalized or the payload is rejected. Branch on the STRUCTURED HTTP status
// (`ApiError.status`, thrown by api.ts throwIfError), never the composed message:
// a transient 5xx whose body echoed a status-like token (e.g. "...API 409...")
// must not be misread as terminal and abandoned without a retry. The string-match
// fallback covers the rare non-ApiError throw (SaveTimeoutError is correctly NOT
// terminal here; only its own message shape would ever reach the fallback).
function isTerminal(err: unknown): boolean {
  if (err instanceof ApiError) {
    return TERMINAL_STATUSES.has(err.status);
  }
  const msg = err instanceof Error ? err.message : String(err);
  return (
    msg.includes("API 400") ||
    msg.includes("API 404") ||
    msg.includes("API 409") ||
    msg.includes("API 410") ||
    msg.includes("API 422")
  );
}

async function runWithRetry(
  task: (signal: AbortSignal) => Promise<unknown>,
): Promise<void> {
  for (let attempt = 0; ; attempt++) {
    // Fresh controller per attempt so a timed-out request is aborted without
    // killing its retry.
    const controller = new AbortController();
    try {
      await withTimeout(task, controller);
      return;
    } catch (err) {
      // A finalized/superseded attempt or a rejected payload won't succeed on
      // replay — abandon this save. The transition that follows (submit/advance)
      // hits the same 409/410 and converges the candidate to Done.
      if (isTerminal(err) || attempt >= RETRY_BACKOFF_MS.length) {
        throw err;
      }
      await sleep(RETRY_BACKOFF_MS[attempt]);
    }
  }
}

/** Which save failed. The task is kept so the retry re-sends the SAME payload. */
export interface AnswerSaveFailure {
  attemptId: string;
  position: number;
  /** "terminal": the server refused it (4xx) — a retry sends the same thing
   *  again and only helps once the cause has gone (a lapsed section, a payload
   *  the server now accepts). "exhausted": every retry was spent on a blip
   *  (`status` is the 5xx of the last try, or null for the network). */
  kind: "terminal" | "exhausted";
  status: number | null;
  task: (signal: AbortSignal) => Promise<unknown>;
  /** The bytes the save sends, when the caller said (see `AnswerSaveMeta`). */
  payloadPrint?: string;
}

/** R13: a save the server refused twice with the same payload (or answered
 *  with a 5xx through two whole ladders, R13b) — let go so the Submit is not
 *  held for ever, and shown to the candidate as a warning. */
export interface AnswerSaveRefusal {
  attemptId: string;
  position: number;
  status: number | null;
  payloadPrint?: string;
}

/** What a task is for — so a failure can name the position and be retried. */
export interface AnswerSaveMeta {
  attemptId: string;
  position: number;
  /** The exact bytes the save sends. With it, a NEW task carrying bytes the
   *  server already refused counts as the same refusal: the per_question
   *  screen re-saves on every press, so without it R14's hold re-armed on each
   *  one after R13 had let the refusal go (verify 2026-10-18). */
  payloadPrint?: string;
}

// `chain` is the tail of the serial execution chain. Each enqueue appends its
// task; each task is wrapped so a rejection can't break the chain for the saves
// behind it (recorded as a failure, never rethrown into the chain). `pending`
// tracks outstanding tasks for hasPendingAnswerSaves().
let chain: Promise<void> = Promise.resolve();
let pending = 0;

// OPS-28 #1: the saves that were given up on, oldest first. One entry per
// POSITION — a newer save for the same position replaces the older failure
// (last-write-wins holds for failures too), and a success for the position
// clears it (the newer write made the lost one moot).
let failures: AnswerSaveFailure[] = [];
// R13: the saves let go after a second refusal, one per position, oldest first.
let refusals: AnswerSaveRefusal[] = [];
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

const samePosition =
  (meta: AnswerSaveMeta) => (f: { attemptId: string; position: number }) =>
    f.attemptId === meta.attemptId && f.position === meta.position;

function dropFailure(meta: AnswerSaveMeta): void {
  const before = failures.length + refusals.length;
  const same = samePosition(meta);
  failures = failures.filter((f) => !same(f));
  // A newer write for the position made a refused one moot too.
  refusals = refusals.filter((r) => !same(r));
  if (failures.length + refusals.length !== before) notify();
}

// Which abandoned saves are LOST answers worth holding the Submit for. A 409 /
// 410 / 404 is the server converging the attempt (finalized, paused, a lapsed
// section's clock — the time-up save fires at 0 remaining and is refused by
// construction; the pre-deadline provisional save carried the answer), and the
// transition that follows routes the candidate accordingly; holding the Submit
// on those would block every sectioned candidate whose clock ran out. What is
// retained: a payload the server refused (400 / 422) and every retry spent on
// a blip (network, 5xx, 429, timeouts) — the two cases where the screen said
// "saved" and the server holds nothing.
function isRetained(err: unknown): boolean {
  if (err instanceof ApiError) {
    return (
      err.status === 400 ||
      err.status === 422 ||
      // verify 2026-10-26: a ladder spent on the save route's 60/min limit is
      // a blip too — the server took nothing. Kept like the network (never
      // let go as a refusal: a rate limit always clears).
      err.status === 429 ||
      isServerFault(err.status)
    );
  }
  return !isTerminal(err);
}

// R13b: the server answered, with a 5xx, on the ladder's last try.
const isServerFault = (status: number | null | undefined): boolean =>
  status != null && status >= 500;

function recordFailure(
  meta: AnswerSaveMeta,
  task: (signal: AbortSignal) => Promise<unknown>,
  err: unknown,
): void {
  // The server converged (409 / 410 / 404 — a locked section, a lapsed clock,
  // a finalized attempt): this save can never land, so an older kept failure
  // for the position goes too. Without this, the Retry of a failure from a
  // section that has since closed answered 409 `section_locked` and the
  // failure stayed, holding every later Submit for good.
  if (!isRetained(err)) {
    dropFailure(meta);
    return;
  }
  const status = err instanceof ApiError ? err.status : null;
  const kind: AnswerSaveFailure["kind"] = isTerminal(err) ? "terminal" : "exhausted";
  const same = samePosition(meta);
  const prior = failures.find(same);
  const refused = refusals.find(same);
  const sameBytes = (print: string | undefined) =>
    meta.payloadPrint !== undefined && print === meta.payloadPrint;
  // R13b: a ladder spent on a 5xx is let go on its second time, like a
  // refusal — but only matched against a kept failure of the same class (a
  // 422 then a 500 is one of each, held once more).
  const serverFault = kind === "exhausted" && isServerFault(status);
  const priorSameClass =
    prior !== undefined &&
    prior.kind === kind &&
    (kind === "terminal" || isServerFault(prior.status));
  if (
    (kind === "terminal" || serverFault) &&
    ((priorSameClass && (prior.task === task || sameBytes(prior.payloadPrint))) ||
      sameBytes(refused?.payloadPrint))
  ) {
    // R13: the same bytes were refused again — the kept refusal's Retry, a
    // second press re-saving them, or a re-save of bytes already let go.
    failures = failures.filter((f) => !same(f));
    refusals = refusals.filter((r) => !same(r)).concat([{ ...meta, status }]);
    notify();
    // Reported when a kept refusal is let go; a re-save of one already let
    // go is not news.
    if (prior) {
      captureSilentFailure("answer_save_refused_twice", {
        status: status ?? "network",
        position: meta.position,
      });
    }
    return;
  }
  failures = failures.filter((f) => !same(f)).concat([{ ...meta, kind, status, task }]);
  refusals = refusals.filter((r) => !same(r));
  notify();
  // Closed vocabulary: the kind, the status, the position — never the answer.
  captureSilentFailure("answer_save_abandoned", {
    kind,
    status: status ?? "network",
    position: meta.position,
  });
}

function runTask(
  task: (signal: AbortSignal) => Promise<unknown>,
  meta: AnswerSaveMeta | undefined,
): void {
  pending += 1;
  chain = chain
    .then(() => runWithRetry(task))
    .then(() => {
      if (meta) dropFailure(meta);
    })
    .catch((err: unknown) => {
      // Swallowed so the chain keeps draining later saves — but no longer
      // lost: with `meta` a retained failure (see `isRetained`) is kept for
      // the Submit guard and the retry. Without it (a caller that did not say
      // what it was saving) the old best-effort posture stands.
      if (meta) recordFailure(meta, task, err);
    })
    .finally(() => {
      pending -= 1;
    });
}

/**
 * Queue a durable answer save. Returns immediately — the POST runs in the
 * background, serialized behind any earlier saves. Never rejects to the caller;
 * durability is guaranteed only up to the next flushAnswerSaves() barrier. The
 * task receives a per-attempt AbortSignal it MUST forward to fetch, so a stalled
 * request is aborted on timeout instead of landing late.
 *
 * `meta` names the position being saved: with it, a permanently-failed save is
 * kept in `answerSaveFailures()` for the Submit guard and the retry.
 */
export function enqueueAnswerSave(
  task: (signal: AbortSignal) => Promise<unknown>,
  meta?: AnswerSaveMeta,
): void {
  runTask(task, meta);
}

/**
 * Resolve once every queued + in-flight save has settled (succeeded or been
 * abandoned after retries). Await this before POST /submit and POST
 * /advance-section so grading/section-lock never runs ahead of a pending write.
 * Loops until the tail is stable so saves enqueued mid-flush are still covered.
 */
export async function flushAnswerSaves(): Promise<void> {
  let current: Promise<void>;
  do {
    current = chain;
    await current;
  } while (chain !== current);
}

/** Whether any save is still queued or in flight (diagnostics/tests). */
export function hasPendingAnswerSaves(): boolean {
  return pending > 0;
}

/** The saves given up on — for one attempt, or every attempt. Oldest first. */
export function answerSaveFailures(attemptId?: string): AnswerSaveFailure[] {
  return attemptId === undefined
    ? failures.slice()
    : failures.filter((f) => f.attemptId === attemptId);
}

/** R13: the saves let go after a second refusal — for one attempt, or all. */
export function answerSaveRefusals(attemptId?: string): AnswerSaveRefusal[] {
  return attemptId === undefined
    ? refusals.slice()
    : refusals.filter((r) => r.attemptId === attemptId);
}

/**
 * Re-run every kept failure of the attempt, in order, through the normal retry
 * ladder, and resolve when they have settled. A failure that fails again stays
 * kept (and is reported again) — except a 400 / 422 refused a second time, or
 * a 5xx spending a second ladder (R13b), which is let go into
 * `answerSaveRefusals()` (R13); one that lands is cleared. Returns how many
 * failures remain for the attempt.
 */
export async function retryAnswerSaveFailures(attemptId: string): Promise<number> {
  const toRetry = answerSaveFailures(attemptId);
  for (const f of toRetry) {
    runTask(f.task, { attemptId: f.attemptId, position: f.position, payloadPrint: f.payloadPrint });
  }
  await flushAnswerSaves();
  return answerSaveFailures(attemptId).length;
}

/**
 * Forget the kept failures of an attempt WITHOUT re-sending them — for an
 * attempt that is over (finalized, expired, gone), where a retry can never
 * land. The Submit guard must not outlive the attempt it guards.
 */
export function discardAnswerSaveFailures(attemptId: string): void {
  const before = failures.length + refusals.length;
  failures = failures.filter((f) => f.attemptId !== attemptId);
  refusals = refusals.filter((r) => r.attemptId !== attemptId);
  if (failures.length + refusals.length !== before) notify();
}

/** Subscribe to failure changes (for `useSyncExternalStore`). */
export function subscribeAnswerSaveFailures(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The failure list's identity changes only when its contents do (a stable
 *  snapshot for `useSyncExternalStore`). */
export function answerSaveFailuresSnapshot(): AnswerSaveFailure[] {
  return failures;
}

/** The same, for `answerSaveRefusals()` (R13). */
export function answerSaveRefusalsSnapshot(): AnswerSaveRefusal[] {
  return refusals;
}
